import { useAtomValue } from "jotai"
import { ControlGroup } from "../ControlGroup"
import {
	getTaskPerformanceMeasurementStatus,
	TaskPerformanceMeasurement,
	TaskPerformanceMeasurementName,
	taskPerformanceMeasurements,
} from "@/store/debug/task-performance-state"
import clsx from "clsx"

export const TaskPerformancePanel = () => {
	const measurements = useAtomValue(taskPerformanceMeasurements)

	const renderTime = (time: number) => {
		let formattedTime = time.toFixed(2).toString()
		let unit = "ms"

		if (time > 1000) {
			unit = "s"
			formattedTime = (time / 1000).toFixed(1)
		}

		return (
			<span>
				<strong>{formattedTime}</strong>&nbsp;<span>{unit}</span>
			</span>
		)
	}

	const renderMeasurementLabel = (name: TaskPerformanceMeasurementName) => {
		const status = getTaskPerformanceMeasurementStatus(name)

		let category = "Not Started"
		let color = "bg-base-900"

		switch (status) {
			case "not-started":
				break

			case "in-progress":
				category = "In Progress"
				color = "bg-amber-900"
				break

			case "done":
				category = "Done"
				color = "bg-lime-900"
				break
		}

		return (
			<span
				className={clsx(
					"inline-block rounded px-1 py-0.5 text-xxxs font-bold uppercase",
					color,
				)}
			>
				{category}
			</span>
		)
	}

	const getDisplayName = (name: TaskPerformanceMeasurementName): string => {
		const displayNames: Record<TaskPerformanceMeasurementName, string> = {
			"import-metadata": "Setup: Load EXIF",
			"culling-group": "Cull: Group",
			"culling-score": "Cull: Score & Top Picks",
			"ai-style-apply": "Edit: Apply Style",
			export: "Export: Export",
			publish: "Publish: Upload",
		}
		return displayNames[name]
	}

	const renderMeasurement = (
		name: TaskPerformanceMeasurementName,
		measurement: TaskPerformanceMeasurement,
	) => {
		const status = getTaskPerformanceMeasurementStatus(name)

		const avgTime =
			measurement.taskTimes.length > 0
				? measurement.taskTimes.reduce((acc, curr) => acc + curr, 0) /
					measurement.taskTimes.length
				: 0

		let totalTime = 0

		switch (status) {
			case "in-progress":
				totalTime = performance.now() - measurement.startTime
				break

			case "done":
				totalTime = measurement.endTime - measurement.startTime
				break

			default:
				break
		}

		return (
			<div
				key={"task-performance-measurement-" + name}
				className="select-text rounded-sm bg-base-600/25 p-2"
			>
				<div className="flex w-full items-center justify-between">
					<span className="mb-1 block font-bold">
						{getDisplayName(name)} {renderMeasurementLabel(name)}
					</span>
					{renderTime(totalTime)}
				</div>
				<div className="mt-1 flex items-center justify-between border-t border-t-base-700 pt-1 text-xxs">
					<span className="opacity-50">
						Task Average Time ({measurement.taskTimes.length} items)
					</span>
					{renderTime(avgTime)}
				</div>
			</div>
		)
	}

	return (
		<ControlGroup name="Task Performance" expandKey="taskperformance" isExpanded isExperimental>
			<div className="flex flex-col space-y-2 pt-2 text-xs">
				{Object.keys(measurements).map((name) => {
					return renderMeasurement(
						name as TaskPerformanceMeasurementName,
						measurements[name],
					)
				})}
			</div>
		</ControlGroup>
	)
}
